import React, { memo, useCallback, useMemo, useState } from 'react';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import { ContextMenu, Interpolate, MemberName, Spinner, TableBodyCell, TableRow } from '@wg/wows-react-uikit';

import { root } from '~/preloaded';
import { clanIsFull } from '~/helpers/clan';
import { getPositionY } from '~/helpers/contextMenu';
import { isoToFormattedLocalDate, isoToFormattedLocalTime } from '~/helpers/datetime';
import { floats, percent, thousands } from '~/helpers/formatting';
import { t } from '~/helpers/localization';
import { ROUTES_MAP } from '~/router/routes';

import { ButtonAction, Clan, StatisticsValue, TooltipInfo } from '~/UIKit';
import { TooltipBody, TooltipFooter } from '~/UIKit/components';

import type { IProps as IContextMenuProps } from '@wg/wows-react-uikit/components/ContextMenu/ContextMenu';
import type { NavigateFunction } from 'react-router-dom';
import type { IAccountInvite } from '~/Actions/ActionInvites';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';

const getSenderTooltipPart = (invite: IAccountInvite) => (
  <Interpolate
    useDangerouslySetInnerHTML={true}
    str={t('Приглашение отправил %(senderMember)s %(date)s&nbsp;в&nbsp;%(time)s')}
    senderMember={<MemberName role={invite.sender.role} name={invite.sender.name} isInline />}
    date={isoToFormattedLocalDate(invite.created_at)}
    time={isoToFormattedLocalTime(invite.created_at)}
    parentTag={'p'}
  />
);

const getDeclineBtnTooltipContent = (invite: IAccountInvite) => {
  const expiresAtDate = isoToFormattedLocalDate(invite.expires_at);
  const expiresAtTime = isoToFormattedLocalTime(invite.expires_at);

  return (
    <div>
      <TooltipBody>
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Время для принятия решения по приглашению истекает %(date)s&nbsp;в&nbsp;%(time)s')}
          date={expiresAtDate}
          time={expiresAtTime}
          parentTag={'p'}
        />
        {getSenderTooltipPart(invite)}
      </TooltipBody>
      <TooltipFooter mouseIcon="left">{t('Отклонить приглашение')}</TooltipFooter>
    </div>
  );
};

const getAcceptBtnTooltipContent = (currentAccount: ICurrentAccountState, invite: IAccountInvite) => {
  const isFullClan = clanIsFull(invite.clan);

  if (currentAccount.inClanCooldownTill) {
    return (
      <div>
        <TooltipBody>
          <Interpolate
            useDangerouslySetInnerHTML={true}
            str={t('Вы недавно покинули клан. Вступление в клан станет доступно с %(date)s&nbsp;%(time)s')}
            date={isoToFormattedLocalDate(currentAccount.inClanCooldownTill)}
            time={isoToFormattedLocalTime(currentAccount.inClanCooldownTill)}
            parentTag={'p'}
          />
          <Interpolate
            useDangerouslySetInnerHTML={true}
            str={t('Время для принятия решения по приглашению истекает %(date)s&nbsp;в&nbsp;%(time)s')}
            date={isoToFormattedLocalDate(invite.expires_at)}
            time={isoToFormattedLocalTime(invite.expires_at)}
            parentTag={'p'}
          />
          {getSenderTooltipPart(invite)}
        </TooltipBody>
      </div>
    );
  }

  if (isFullClan) {
    return (
      <div>
        <TooltipInfo>{t('В клане сейчас нет мест')}</TooltipInfo>
        <TooltipBody>
          <Interpolate
            useDangerouslySetInnerHTML={true}
            str={t('Время для принятия решения по приглашению истекает %(date)s&nbsp;в&nbsp;%(time)s')}
            date={isoToFormattedLocalDate(invite.expires_at)}
            time={isoToFormattedLocalTime(invite.expires_at)}
            parentTag={'p'}
          />
          {getSenderTooltipPart(invite)}
        </TooltipBody>
      </div>
    );
  }

  if (!invite.clan.is_active) {
    return (
      <div>
        <TooltipInfo>{t('Клан распущен')}</TooltipInfo>
        <TooltipBody>
          <Interpolate
            useDangerouslySetInnerHTML={true}
            str={t('Время для принятия решения по приглашению истекает %(date)s&nbsp;в&nbsp;%(time)s')}
            date={isoToFormattedLocalDate(invite.expires_at)}
            time={isoToFormattedLocalTime(invite.expires_at)}
            parentTag={'p'}
          />
          {getSenderTooltipPart(invite)}
        </TooltipBody>
      </div>
    );
  }

  return (
    <div>
      <TooltipBody>
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Время для принятия решения по приглашению истекает %(date)s&nbsp;в&nbsp;%(time)s')}
          date={isoToFormattedLocalDate(invite.expires_at)}
          time={isoToFormattedLocalTime(invite.expires_at)}
          parentTag={'p'}
        />
        {getSenderTooltipPart(invite)}
      </TooltipBody>
      <TooltipFooter mouseIcon="left">
        <p>{t('Вступить в клан')}</p>
      </TooltipFooter>
    </div>
  );
};

interface InvitesNonClanMemberTableItemProps {
  navigate: NavigateFunction;
  invite: IAccountInvite;
  isInProcessCancelation: boolean;
  currentAccount: ICurrentAccountState;
  root: string;
  onAcceptedInvite: (invite: IAccountInvite) => void;
  onDeclinedInvite: (invite: IAccountInvite) => void;
}

const InvitesNonClanMemberTableItem = ({
  navigate,
  invite,
  isInProcessCancelation,
  currentAccount,
  onAcceptedInvite,
  onDeclinedInvite,
}: InvitesNonClanMemberTableItemProps) => {
  const [isContextMenuActive, setIsContextMenuActive] = useState(false);
  const [contextMenuContext, setContextMenuContext] = useState<IContextMenuProps['context']>([]);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const clan = invite.clan;
  const isHiddenStatistics = invite.is_hidden_statistics;

  const isAcceptDisabled = useCallback(() => {
    const isFullClan = clanIsFull(invite.clan);
    return !!(currentAccount.inClanCooldownTill || isFullClan || !invite.clan.is_active);
  }, [currentAccount.inClanCooldownTill, invite.clan]);

  const buildContextMenuContext = useCallback((): IContextMenuProps['context'] => {
    let acceptInviteTooltip: string | null = null;

    if (currentAccount.inClanCooldownTill) {
      acceptInviteTooltip = t('Вы недавно покинули клан. Вступление в клан станет доступно %(date)s %(time)s', {
        date: isoToFormattedLocalDate(currentAccount.inClanCooldownTill),
        time: isoToFormattedLocalTime(currentAccount.inClanCooldownTill),
      });
    } else if (clanIsFull(invite.clan)) {
      acceptInviteTooltip = t('В клане сейчас нет мест');
    } else if (!invite.clan.is_active) {
      acceptInviteTooltip = t('Клан распущен');
    }

    const baseItems: IContextMenuProps['context'][number]['items'] = [];

    if (invite.clan.is_active) {
      baseItems.push(
        {
          name: t('Профиль клана'),
          value: 'CLAN_PROFILE',
          disabled: false,
        },
        {
          name: t('Вступить в клан'),
          disabled: isInProcessCancelation || isAcceptDisabled(),
          tooltip: acceptInviteTooltip ?? undefined,
          value: 'ACCEPT_INVITE',
        },
      );
    }

    baseItems.push({
      name: t('Отклонить приглашение'),
      disabled: isInProcessCancelation,
      value: 'DECLINE_INVITE',
    });

    return [
      {
        isNeedDivider: false,
        items: baseItems,
      },
    ];
  }, [currentAccount.inClanCooldownTill, invite.clan, isAcceptDisabled, isInProcessCancelation]);

  const onContextMenu = useCallback<React.MouseEventHandler<HTMLElement>>(
    (event) => {
      const context = buildContextMenuContext();
      const clientY = getPositionY(event, context);

      setContextMenuContext(context);
      setContextMenuPosition({
        x: event.clientX,
        y: clientY,
      });
    },
    [buildContextMenuContext],
  );

  const onContextMenuItemClick = useCallback<IContextMenuProps['onItemClick']>(
    (action) => {
      switch (action) {
        case 'CLAN_PROFILE': {
          navigate(`${root}${ROUTES_MAP.CLAN_PROFILE.replace(':clanId', String(invite.clan.id))}`);
          break;
        }

        case 'ACCEPT_INVITE': {
          onAcceptedInvite(invite);
          break;
        }

        case 'DECLINE_INVITE': {
          onDeclinedInvite(invite);
          break;
        }
      }
    },
    [invite, navigate, onAcceptedInvite, onDeclinedInvite],
  );

  const onContextMenuStateChange = useCallback((nextIsActive: boolean) => {
    setIsContextMenuActive(nextIsActive);
  }, []);

  const onClanClick = useCallback(() => {
    if (!clan.is_active) {
      return;
    }

    void playButtonClickSound();
    navigate(`${root}${ROUTES_MAP.CLAN_PROFILE.replace(':clanId', String(clan.id))}`);
  }, [clan.id, clan.is_active, navigate]);

  const acceptBtnTooltipContent = useMemo(() => {
    if (isContextMenuActive) {
      return null;
    }

    return getAcceptBtnTooltipContent(currentAccount, invite);
  }, [currentAccount, invite, isContextMenuActive]);

  const declineBtnTooltipContent = useMemo(() => {
    if (isContextMenuActive) {
      return null;
    }

    return getDeclineBtnTooltipContent(invite);
  }, [invite, isContextMenuActive]);

  let actionCell: React.ReactNode = null;

  if (isInProcessCancelation) {
    actionCell = (
      <TableBodyCell modify="right">
        <Spinner />
      </TableBodyCell>
    );
  } else {
    actionCell = (
      <TableBodyCell modify="buttons">
        <ButtonAction
          action="ok"
          isDisabled={isAcceptDisabled()}
          onClick={() => {
            onAcceptedInvite(invite);
          }}
          tooltipContent={acceptBtnTooltipContent}
        />
        <ButtonAction
          action="cancel"
          onClick={() => {
            onDeclinedInvite(invite);
          }}
          tooltipContent={declineBtnTooltipContent}
        />
      </TableBodyCell>
    );
  }

  return (
    <TableRow isHovering isActive={isContextMenuActive} onContextMenu={onContextMenu} onClick={onClanClick}>
      <ContextMenu
        context={contextMenuContext}
        onContextMenuStateChange={onContextMenuStateChange}
        onItemClick={onContextMenuItemClick}
        position={contextMenuPosition}
        isDemo={false}
      />

      <TableBodyCell modify="basis">
        <Clan
          clanTag={clan.tag}
          clanColor={clan.color}
          clanName={clan.name}
          isDisbanded={!clan.is_active}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="small">{clan.members_count}</TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={thousands(invite.statistics.abtl)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={percent(invite.statistics.awb, undefined)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={thousands(invite.statistics.aeb)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={thousands(invite.statistics.admg)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          isClan
          value={floats(invite.statistics.afb, 2)}
          isHidden={isHiddenStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      {actionCell}
    </TableRow>
  );
};

export default memo(InvitesNonClanMemberTableItem);
